import {Grid} from '@yandex-delivery/blocks'
import {css} from '@yandex-delivery/blocks/theme/css'
import {spacing} from '@yandex-delivery/blocks/theme/theme'
import React, {useCallback} from 'react'
import type {ContentListItem} from 'src/api/types'
import {isAuthMode} from 'src/shared/config'
import {formatPhone, tagToUrl} from 'src/shared/utils'

import {ExtendExpirationButton} from '../extend-expiration-action'
import {TextItem} from '../text-item'
import {getActionPayload} from './ListItem.utils'

type Props = {
  item: ContentListItem
}

const classes = {
  image: css`
    height: 24px;
  `,
  right: css`
    text-align: right;
  `,
  payloadChip: css`
    display: inline-flex;
    align-items: center;
    padding: ${spacing(0.5, 1.5)};
    border-radius: 100px;
    border: none;
    cursor: pointer;
    background-color: var(--colorScheme-controlMinor, rgba(245, 244, 242, 0.1));
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
    color: var(--colorScheme-text, #21201F);
    white-space: nowrap;
    flex-shrink: 0;
  `,
}

export const ListItem: React.FC<Props> = ({item}) => {
  const {title, subtitle, trail_text: trailTitle, trail_subtitle: trailSubtitle, lead_icon: leadIcon} = item
  const copiedSubtitle = subtitle ? {...subtitle} : undefined

  if (copiedSubtitle && copiedSubtitle.text) {
    const value = isPhone(item) ? formatPhone(copiedSubtitle.text) : copiedSubtitle.text
    copiedSubtitle.text = value || ''
  }

  // Check both general_payload and trail_payload
  const actionPayload = getActionPayload(item)

  const handlePayloadPress = useCallback(() => {
    if (!actionPayload || !isAuthMode) return
    window.taxiApp?.call('handlePayloadAction', 'token', {
      action_type: actionPayload.action.type,
    })
  }, [actionPayload])

  // Special case: extend_delivery_expiration_date should use ExtendExpirationButton
  const isExtendExpiration = actionPayload?.action.type === 'extend_delivery_expiration_date'

  if (isExtendExpiration && isAuthMode) {
    return (
      <ExtendExpirationButton
        action={{
          type: 'extend_delivery_expiration_date',
          title: actionPayload?.action.title || '',
          badge_text: subtitle?.text || '',
        }}
        leadIcon={leadIcon}
      />
    )
  }

  return (
    <Grid alignItems="center" container direction="row" spacing={1}>
      {leadIcon && (
        <Grid item>
          <Grid alignItems="center" container item>
            <img alt="" className={classes.image} data-theme-invert loading="lazy" src={tagToUrl(leadIcon.image_tag)} />
          </Grid>
        </Grid>
      )}
      <Grid alignItems="center" container item justify="space-between" wrap="nowrap" xs>
        <Grid item>
          <TextItem item={title} />
          {copiedSubtitle && <TextItem item={copiedSubtitle} />}
        </Grid>
        {actionPayload?.action.title && (
          <Grid item>
            <button className={classes.payloadChip} onClick={handlePayloadPress} type="button">
              {actionPayload.action.title}
            </button>
          </Grid>
        )}
        {!actionPayload && (trailTitle || trailSubtitle) && (
          <Grid className={classes.right} item>
            {trailTitle && <TextItem item={trailTitle} />}
            {trailSubtitle && <TextItem item={trailSubtitle} />}
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

function isPhone(item: Props['item']): boolean {
  const ITEM_LEAD_ICON_IMAGE_TAG = 'delivery_phone'

  return item.lead_icon?.image_tag === ITEM_LEAD_ICON_IMAGE_TAG
}
